'use client';

import {
  Children,
  MouseEvent,
  ReactElement,
  ReactNode,
  TouchEvent,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { icons } from '@/assets/icons';
import styles from './ResultsCarousel.module.scss';

interface ResultsCarouselProps {
  children: ReactNode;
  className?: string;
  controlsClassName?: string;
}

type CarouselItemProps = {
  isActive?: boolean;
};

// Physics and visual constants
const GAP = 20;
// Strength of the spring (0-1). Higher is stiffer.
const SPRING_STRENGTH = 0.1;
const DRAG_DAMPING = 0.5;
const SNAP_DAMPING = 0.5;
const TOUCH_SENSITIVITY = 1; // New constant for mobile touch drag sensitivity
// Fraction of card width that needs to be dragged to trigger slide change on touch
const SWIPE_THRESHOLD_TOUCH = 0.28; // ~28% for touch

export default function ResultsCarousel({
  children,
  className = '',
  controlsClassName = '',
}: ResultsCarouselProps) {
  const items = useMemo(
    () => Children.toArray(children) as ReactElement<CarouselItemProps>[],
    [children]
  );

  const viewportRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Start with index 2 (3rd item) active by default, or middle if available
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const state = useRef({
    scrollX: 0,
    targetX: 0, // Current target for snapping
    velocity: 0,
    isDragging: false,
    isSnapping: false,
    startX: 0, // pointer X at drag start
    startY: 0, // Added for vertical swipe check
    lastX: 0,
    startScrollX: 0, // scroll position at drag start
    itemWidth: 0,
    trackWidth: 0,
    initialized: false,
    hasDragged: false,
    lastIsTouch: false,
  });

  const requestRef = useRef<number | null>(null);
  const isAnimating = useRef(false);

  const mod = (n: number, m: number) => ((n % m) + m) % m;

  const update = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !state.current.initialized) {
      isAnimating.current = false;
      return;
    }

    const s = state.current;
    let shouldContinue = false;

    if (s.isDragging) {
      shouldContinue = true;
      // During drag, velocity comes from drag delta
    } else if (s.isSnapping) {
      // Spring physics towards targetX
      const dist = s.targetX - s.scrollX;
      const force = dist * SPRING_STRENGTH;
      s.velocity += force;
      s.velocity *= SNAP_DAMPING;
      s.scrollX += s.velocity;

      // Stop snapping if close enough
      if (Math.abs(dist) < 0.5 && Math.abs(s.velocity) < 0.1) {
        s.scrollX = s.targetX;
        s.velocity = 0;
        s.isSnapping = false;
      } else {
        shouldContinue = true;
      }
    } else {
      // Free momentum decay
      if (Math.abs(s.velocity) > 0.01) {
        s.scrollX += s.velocity;
        s.velocity *= DRAG_DAMPING;
        shouldContinue = true;
      } else {
        s.velocity = 0;
      }
    }

    // Only update DOM if we are animating or dragging

    const step = s.itemWidth + GAP;
    const halfTrack = s.trackWidth / 2;
    let closestDist = Infinity;
    let closestIndex = -1;

    // Always run DOM update if we are in this loop, because we are either moving or just settled
    items.forEach((_, i) => {
      const slide = slideRefs.current[i];
      if (!slide) return;

      const itemX = i * step;
      let pos = itemX - s.scrollX;

      // Wrap logic
      while (pos < -halfTrack) pos += s.trackWidth;
      while (pos > halfTrack) pos -= s.trackWidth;

      // Direct style update for performance
      slide.style.setProperty('translate', `${pos}px`);

      const dist = Math.abs(pos);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    if (closestIndex !== -1 && closestIndex !== activeIndexRef.current) {
      setActiveIndex(closestIndex);
    }

    if (shouldContinue) {
      requestRef.current = requestAnimationFrame(update);
    } else {
      isAnimating.current = false;
    }
  }, [items]);

  const startLoop = useCallback(() => {
    if (!isAnimating.current) {
      isAnimating.current = true;
      requestRef.current = requestAnimationFrame(update);
    }
  }, [update]);

  // --- Measurement & Resize ---
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || items.length === 0) return;

    const measure = () => {
      const firstSlide = slideRefs.current[0];
      if (!firstSlide) return;

      const rect = firstSlide.getBoundingClientRect();
      if (rect.width === 0) return; // hidden or unmounted

      const newWidth = rect.width;
      const oldWidth = state.current.itemWidth;

      // Only update if changed significantly
      if (Math.abs(newWidth - oldWidth) > 0.5) {
        state.current.itemWidth = newWidth;
        const newStep = newWidth + GAP;
        state.current.trackWidth = items.length * newStep;
        viewport.style.height = `${rect.height + 180}px`; // Space for cards

        if (!state.current.initialized) {
          // Initial setup: Center start index
          const startIdx = Math.max(0, Math.min(2, items.length - 1));
          const startPos = startIdx * newStep;
          state.current.scrollX = startPos;
          state.current.targetX = startPos;
          setActiveIndex(startIdx);
          state.current.initialized = true;
        } else {
          // Resize: Snap to currently active card to prevent drifting
          const activePos = activeIndexRef.current * newStep;
          state.current.scrollX = activePos;
          state.current.targetX = activePos;
          state.current.isSnapping = false;
          state.current.velocity = 0;
        }
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (slideRefs.current[0]) {
      observer.observe(slideRefs.current[0]);
    }

    window.addEventListener('resize', measure);
    // Start loop initially
    startLoop();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      isAnimating.current = false;
    };
  }, [items.length, update, startLoop]);

  // --- Helpers ---

  const snapTo = (index: number) => {
    const s = state.current;
    const step = s.itemWidth + GAP;

    // Handle wrapping for shortest path
    const count = items.length;
    const normalizedIndex = mod(index, count);
    const targetItemX = normalizedIndex * step;

    // Find shortest distance
    let dist = targetItemX - s.scrollX;
    const half = s.trackWidth / 2;

    while (dist < -half) dist += s.trackWidth;
    while (dist > half) dist -= s.trackWidth;

    s.targetX = s.scrollX + dist;
    s.isSnapping = true;
    startLoop();
  };

  // --- Event Handlers ---
  const handleDragStart = (startX: number, startY: number, isTouch = false) => {
    state.current.isDragging = true;
    state.current.isSnapping = false;
    state.current.startX = startX;
    state.current.startY = startY;
    state.current.lastX = startX;
    state.current.startScrollX = state.current.scrollX;
    state.current.velocity = 0;
    state.current.hasDragged = false;
    state.current.lastIsTouch = isTouch;
    if (viewportRef.current) {
      viewportRef.current.style.cursor = 'grabbing';
    }
    startLoop();
  };

  const handleDragMove = (
    currentX: number,
    currentY: number,
    isTouch = false
  ) => {
    if (!state.current.isDragging) return;

    // Vertical scroll check for touch
    if (isTouch && !state.current.hasDragged) {
      const deltaX = Math.abs(currentX - state.current.startX);
      const deltaY = Math.abs(currentY - state.current.startY);
      // If moving more vertically than horizontally, release drag to allow scroll
      if (deltaY > deltaX && deltaY > 10) {
        state.current.isDragging = false;
        if (viewportRef.current) {
          viewportRef.current.style.cursor = 'grab';
        }
        return;
      }
    }

    const delta = currentX - state.current.lastX;
    state.current.lastX = currentX;

    if (Math.abs(currentX - state.current.startX) > 5) {
      state.current.hasDragged = true;
    }

    const sensitivity = isTouch ? TOUCH_SENSITIVITY : 1;
    state.current.scrollX -= delta * sensitivity;

    if (!isAnimating.current) startLoop();
  };

  const handleDragEnd = () => {
    state.current.isDragging = false;
    if (viewportRef.current) {
      viewportRef.current.style.cursor = 'grab';
    }

    const s = state.current;
    if (!s.itemWidth) return;

    const step = s.itemWidth + GAP;

    // --- Desktop / mouse: keep original behavior ---
    if (!s.lastIsTouch) {
      const rawIdx = s.scrollX / step;
      const currentIdx = Math.round(rawIdx);

      const threshold = step * 0.1;
      let targetIdx = currentIdx;
      const startIdx = Math.round(s.startX / step);

      if (s.scrollX > s.startX + threshold) {
        if (targetIdx <= startIdx) {
          targetIdx = startIdx + 1;
        }
      } else if (s.scrollX < s.startX - threshold) {
        if (targetIdx >= startIdx) {
          targetIdx = startIdx - 1;
        }
      }

      s.targetX = targetIdx * step;
      s.isSnapping = true;
      startLoop();
      return;
    }

    // --- Touch: use smaller swipe threshold based on scroll delta ---
    const activeIdx = activeIndexRef.current;
    const delta = s.scrollX - s.startScrollX;
    const threshold = step * SWIPE_THRESHOLD_TOUCH;

    let targetIdx = activeIdx;

    if (delta > threshold) {
      // dragged left → move to next card
      targetIdx = activeIdx + 1;
    } else if (delta < -threshold) {
      // dragged right → move to previous card
      targetIdx = activeIdx - 1;
    }

    snapTo(targetIdx);
  };

  const onMouseDown = (e: MouseEvent) =>
    handleDragStart(e.clientX, e.clientY, false);
  const onMouseMove = (e: MouseEvent) =>
    handleDragMove(e.clientX, e.clientY, false);
  const onMouseUp = () => handleDragEnd();
  const onMouseLeave = () => handleDragEnd();

  const onTouchStart = (e: TouchEvent) =>
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY, true);
  const onTouchMove = (e: TouchEvent) =>
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY, true);
  const onTouchEnd = () => handleDragEnd();
  const onTouchCancel = () => handleDragEnd();

  return (
    <div className={clsx(styles.carousel, className)}>
      <div
        className={styles.viewport}
        ref={viewportRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        {items.map((child, idx) => (
          <div
            key={child.key ?? `artist-card-${idx}`}
            className={styles.slide}
            ref={(el) => {
              slideRefs.current[idx] = el;
            }}
            data-card-index={idx}
            onClick={() => {
              if (!state.current.hasDragged) {
                snapTo(idx);
              }
            }}
          >
            {cloneElement(child, {
              isActive: idx === activeIndex,
            })}
          </div>
        ))}
      </div>

      <div className={clsx(styles.controls, controlsClassName)}>
        <div className={styles.navButtons}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Show previous result"
            onClick={() => snapTo(activeIndex - 1)}
          >
            <svg className={clsx(styles.navIcon, styles.prevIcon)}>
              <use href={`${icons.src}#icon-arrow_rounded`} />
            </svg>
          </button>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Show next result"
            onClick={() => snapTo(activeIndex + 1)}
          >
            <svg className={clsx(styles.navIcon, styles.nextIcon)}>
              <use href={`${icons.src}#icon-arrow_rounded`} />
            </svg>
          </button>
        </div>

        <div className={styles.progress}>
          {items.map((_, idx) => (
            <button
              key={`dot-${idx}`}
              type="button"
              className={clsx(
                styles.progressDot,
                idx === activeIndex && styles.progressDotActive
              )}
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => snapTo(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
